/**
 * URL state for `/reports/outgoing`.
 *
 * Date params use Organization-timezone calendar days as `YYYY-MM-DD`.
 * When absent from the URL, the API applies its default last-30-days range in
 * Organization.timezone and echoes resolved fromDate/toDate on the summary.
 * Explicit URL dates are sent as fromDate/toDate (never browser-local ISO).
 */
import {
  DEFAULT_REPORT_MESSAGE_PAGE_SIZE,
  DEFAULT_REPORT_MESSAGE_SORT,
  MESSAGE_ORIGINS,
  MESSAGE_STATUSES,
  PLATFORM_TYPES,
  REPORT_MESSAGE_PAGE_SIZES,
  REPORT_MESSAGE_SORTS,
  REPORT_MESSAGE_STATUS_BUCKETS,
  type MessageOriginName,
  type MessageStatusName,
  type PlatformTypeName,
  type ReportMessagePageSize,
  type ReportMessageSortName,
  type ReportMessageStatusBucketName,
} from "@multi-chat/shared";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export type OutgoingReportQuery = {
  /**
   * Explicit Organization-calendar day YYYY-MM-DD (inclusive).
   * Undefined ⇒ API default date range (org TZ).
   */
  from?: string;
  to?: string;
  search: string;
  platform?: PlatformTypeName;
  origin?: MessageOriginName;
  /** Prefer statusBucket for summary-card filters; mutually exclusive with status. */
  statusBucket?: ReportMessageStatusBucketName;
  status?: MessageStatusName;
  endpointId?: number;
  campaignId?: number;
  template?: string;
  page: number;
  pageSize: ReportMessagePageSize;
  sort: ReportMessageSortName;
};

function parseYmd(raw: string | null): string | null {
  if (raw == null || !YMD_RE.test(raw)) return null;
  const parts = raw.split("-").map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (y == null || m == null || d == null) return null;
  // Structural validity only — calendar interpretation is Organization timezone on the API.
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return raw;
}

function parsePositiveInt(raw: string | null): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
}

function isPlatform(value: string | null): value is PlatformTypeName {
  return value != null && (PLATFORM_TYPES as readonly string[]).includes(value);
}

function isOrigin(value: string | null): value is MessageOriginName {
  return value != null && (MESSAGE_ORIGINS as readonly string[]).includes(value);
}

function isStatusBucket(value: string | null): value is ReportMessageStatusBucketName {
  return value != null && (REPORT_MESSAGE_STATUS_BUCKETS as readonly string[]).includes(value);
}

function isStatus(value: string | null): value is MessageStatusName {
  return value != null && (MESSAGE_STATUSES as readonly string[]).includes(value);
}

function isSort(value: string | null): value is ReportMessageSortName {
  return value != null && (REPORT_MESSAGE_SORTS as readonly string[]).includes(value);
}

export function parseOutgoingReportQuery(search: string): OutgoingReportQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const from = parseYmd(params.get("from"));
  const to = parseYmd(params.get("to"));
  const pageSizeRaw = Number(params.get("pageSize"));
  const pageSize = (REPORT_MESSAGE_PAGE_SIZES as readonly number[]).includes(pageSizeRaw)
    ? (pageSizeRaw as ReportMessagePageSize)
    : DEFAULT_REPORT_MESSAGE_PAGE_SIZE;
  const sortRaw = params.get("sort");
  const platformRaw = params.get("platform");
  const originRaw = params.get("origin");
  const statusBucketRaw = params.get("statusBucket");
  const statusRaw = params.get("status");
  const statusBucket = isStatusBucket(statusBucketRaw) ? statusBucketRaw : undefined;
  const status =
    statusBucket == null && isStatus(statusRaw) ? statusRaw : undefined;
  const endpointId = parsePositiveInt(params.get("endpointId"));
  const campaignId = parsePositiveInt(params.get("campaignId"));
  const templateRaw = params.get("template")?.trim();

  const result: OutgoingReportQuery = {
    search: (params.get("search") ?? "").trim().slice(0, 200),
    page: parsePositiveInt(params.get("page")) ?? 1,
    pageSize,
    sort: isSort(sortRaw) ? sortRaw : DEFAULT_REPORT_MESSAGE_SORT,
  };
  // Require both calendar ends; otherwise fall back to API default range.
  if (from != null && to != null) {
    result.from = from <= to ? from : to;
    result.to = from <= to ? to : from;
  }
  if (isPlatform(platformRaw)) result.platform = platformRaw;
  if (isOrigin(originRaw)) result.origin = originRaw;
  if (statusBucket) result.statusBucket = statusBucket;
  if (status) result.status = status;
  if (endpointId != null) result.endpointId = endpointId;
  if (campaignId != null) result.campaignId = campaignId;
  if (templateRaw) result.template = templateRaw.slice(0, 200);
  return result;
}

export function outgoingReportHref(query: OutgoingReportQuery): string {
  const params = new URLSearchParams();
  if (query.from && query.to) {
    params.set("from", query.from);
    params.set("to", query.to);
  }
  if (query.search.trim().length > 0) params.set("search", query.search.trim());
  if (query.platform) params.set("platform", query.platform);
  if (query.origin) params.set("origin", query.origin);
  if (query.statusBucket) params.set("statusBucket", query.statusBucket);
  else if (query.status) params.set("status", query.status);
  if (query.endpointId != null) params.set("endpointId", String(query.endpointId));
  if (query.campaignId != null) params.set("campaignId", String(query.campaignId));
  if (query.template?.trim()) params.set("template", query.template.trim());
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_REPORT_MESSAGE_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  if (query.sort !== DEFAULT_REPORT_MESSAGE_SORT) params.set("sort", query.sort);
  const qs = params.toString();
  return qs.length > 0 ? `/reports/outgoing?${qs}` : "/reports/outgoing";
}

/** True when search, explicit dates, or non-default filters are set. */
export function hasActiveOutgoingFilters(query: OutgoingReportQuery): boolean {
  if (query.search.trim().length > 0) return true;
  if (query.from != null || query.to != null) return true;
  if (query.platform) return true;
  if (query.origin) return true;
  if (query.statusBucket) return true;
  if (query.status) return true;
  if (query.endpointId != null) return true;
  if (query.campaignId != null) return true;
  if (query.template?.trim()) return true;
  return false;
}

export function clearOutgoingFilters(query: OutgoingReportQuery): OutgoingReportQuery {
  return {
    search: "",
    page: 1,
    pageSize: query.pageSize,
    sort: query.sort,
  };
}
